import { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { tryAutoLoginQuickUser, clearQuickCred } from '@/lib/auth/quickSignup';

export type AccountTier = 'guest' | 'quick' | 'full';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isEmailConfirmed: boolean;
  isAnonymous: boolean;
  tier: AccountTier;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isEmailConfirmed: false,
  isAnonymous: false,
  tier: 'guest',
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const finishLoading = () => {
      if (!cancelled) setLoading(false);
    };

    // Hard safety net: never let the app sit on a spinner for more than 4s
    // because of a hung auth bootstrap (slow Supabase, blocked network, etc.).
    // Downstream UI that gates on `loading` should always get a chance to render.
    const safetyTimer = setTimeout(finishLoading, 4000);

    // 1. Subscribe FIRST to catch any auth events during getSession
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Clear user on explicit sign-out OR on token-refresh failure.
      // A null session on TOKEN_REFRESHED means the refresh token is invalid
      // (e.g. rotated, revoked, or "missing sub claim" / bad_jwt errors seen in
      // auth logs) — keeping a stale user causes 403s on every authenticated
      // request until full reload.
      if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session)) {
        setUser(null);
        finishLoading();
        if (event === 'TOKEN_REFRESHED' && !session) {
          supabase.auth.signOut({ scope: 'local' }).catch(() => {});
        }
        if (event === 'SIGNED_OUT') {
          // User explicitly signed out — forget quick-signup creds so we don't
          // silently re-login them on the next page load.
          clearQuickCred();
        }
      } else if (session) {
        // For TOKEN_REFRESHED, SIGNED_IN, INITIAL_SESSION, USER_UPDATED etc.
        // only update if session is non-null
        setUser(session.user);
        finishLoading();
      } else if (event === 'INITIAL_SESSION') {
        // Guest visit (no session, no error) — release the spinner immediately
        // so the public homepage renders without waiting on getSession()'s chain.
        setUser(null);
        finishLoading();
      }
    });

    // 2. THEN get session to handle already-authenticated state
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      // Any session retrieval error (expired/invalid refresh token, bad JWT,
      // missing sub claim, etc.) — clear local session quietly so the user
      // lands on a logged-out state instead of seeing repeated 403s.
      if (error) {
        supabase.auth.signOut({ scope: 'local' }).catch(() => {});
        setUser(null);
        finishLoading();
      } else {
        setUser(session?.user ?? null);
        // No active session — try silent re-login for returning quick-signup
        // users (same IP). onAuthStateChange will pick up the new session.
        if (!session) {
          // Don't hijack an in-flight OAuth callback: the code exchange can
          // still be running, and signing in a quick/guest user here would
          // overwrite the Google session that is about to land.
          const href = typeof window !== 'undefined' ? window.location.href : '';
          const isOAuthCallback =
            /[?&#](code|access_token|error|error_description)=/.test(href);
          if (isOAuthCallback) {
            finishLoading();
            return;
          }
          tryAutoLoginQuickUser().finally(finishLoading);
          return;
        }
        // Validate the cached session against the server. getSession() only
        // reads localStorage, so a token invalidated by signing-key rotation
        // (auth logs: "missing sub claim" / bad_jwt) still looks valid here.
        // If getUser() fails, sign out locally so the app doesn't sit in a
        // half-logged-in state where every authenticated request 403s.
        supabase.auth.getUser().then(({ error: userError }) => {
          if (userError) {
            const msg = String(userError.message || '').toLowerCase();
            const code = String((userError as { code?: string }).code || '').toLowerCase();
            const isStaleToken =
              code === 'bad_jwt' ||
              msg.includes('missing sub') ||
              msg.includes('jwt') ||
              msg.includes('invalid claim');
            if (isStaleToken) {
              supabase.auth.signOut({ scope: 'local' }).catch(() => {});
              setUser(null);
            }
          }
        }).catch(() => {
          // Network failure — leave the cached session in place.
        }).finally(finishLoading);
      }
    }).catch(() => {
      supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      setUser(null);
      finishLoading();
    });

    return () => {
      cancelled = true;
      clearTimeout(safetyTimer);
      subscription.unsubscribe();
    };
  }, []);

  const isEmailConfirmed = !!user?.email_confirmed_at;
  // Supabase anonymous users carry `is_anonymous: true` on the user object
  const isAnonymous = !!(user as any)?.is_anonymous;
  const tier: AccountTier = !user ? 'guest' : isAnonymous ? 'quick' : 'full';

  return (
    <AuthContext.Provider value={{ user, loading, isEmailConfirmed, isAnonymous, tier }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
